import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import BrandLogo from "@/components/marketing/BrandLogo";
import { KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    if (!token) return toast.error("Missing reset token — request a new link.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-[6px] border border-[#E2E8F0] p-8 shadow-sm">
        <div className="mb-5"><BrandLogo size="md" /></div>
        {!done ? (
          <>
            <div className="w-12 h-12 rounded-[6px] bg-[#F97316]/12 text-[#F97316] flex items-center justify-center mb-5">
              <KeyRound className="w-6 h-6" />
            </div>
            <h1 className="font-display font-extrabold text-2xl">Set a new password</h1>
            <p className="text-sm text-[#4B5563] mt-2">Pick something only you would guess. Minimum 6 characters.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1.5">New password</label>
                <input
                  data-testid="reset-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full border border-[#E2E8F0] focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/20 outline-none px-3 py-2.5 rounded-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1.5">Confirm new password</label>
                <input
                  data-testid="reset-confirm-input"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  className="w-full border border-[#E2E8F0] focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/20 outline-none px-3 py-2.5 rounded-sm"
                />
              </div>
              <button
                data-testid="reset-submit-button"
                type="submit"
                disabled={busy || !token}
                className="btn-primary w-full disabled:opacity-60"
              >
                {busy ? "Resetting…" : "Reset password"}
              </button>
            </form>
            {!token && (
              <p className="text-xs text-[#EF4444] mt-4">Missing or invalid token. <Link to="/forgot-password" className="underline">Request a new link</Link>.</p>
            )}
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-[6px] bg-[#10B981]/12 text-[#10B981] flex items-center justify-center mb-5">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h1 className="font-display font-extrabold text-2xl" data-testid="reset-success-heading">Password updated</h1>
            <p className="text-sm text-[#4B5563] mt-2">Redirecting you to sign in…</p>
          </>
        )}
      </div>
    </div>
  );
}
